// ─────────────────────────────────────────────────────────────────────────
// COUCHE 2 — Score de pertinence (« le matching »)
//   Réf. « Note experte — Architecture de Matching » (Arié & Max, juillet 2026).
//   Répond à : « parmi les opportunités possibles, lesquelles ont le plus de
//   sens pour CE profil exposant ? ». Calculé uniquement sur les paires ayant
//   passé la Couche 1 (éligibilité).
//
//   RÈGLE D'OR : ni la complétion de profil ni la réputation n'entrent ici.
//   Un exposant tout juste inscrit doit pouvoir être aussi bien classé qu'un
//   historique s'il est géographiquement et thématiquement pertinent.
//
//   Pondérations : Région 45 · Affinité 25 · Thème/style 10 · Positionnement
//   prix 10 · Taille 10. Rééquilibrées le 21/07 (base : Région 35 · Thème 25 ·
//   Prix 20 · Affinité 15 · Taille 5) — demande Max : catégorie et besoin
//   technique sont déjà des filtres durs en Couche 1 (éligibilité), donc pas
//   de poids ici ; le style musical compte peu côté score (c'est le festival
//   qui vient chercher le stand, pas l'inverse) mais reste affiché ; Région
//   devient très dominant, Affinité montée pour valoriser les favoris
//   déclarés côté exposant.
//
//   DÉGRADATION GRACIEUSE : un critère dont la donnée est absente (ex. festival
//   non musical → pas de style ; exposant sans favoris → pas d'affinité) renvoie
//   `null` et est RETIRÉ du dénominateur, au lieu de compter 0. Sans cela, un
//   festival non musical plafonnerait mécaniquement à 70/100 et le seuil ≥50
//   deviendrait incohérent. Fonction pure, testable unitairement.
// ─────────────────────────────────────────────────────────────────────────

import { MUSIC_STYLE_WILDCARD } from '../musicStyles';

export interface MatchBreakdown {
  region: number | null;   // 0..1 ou null si non calculable
  theme: number | null;
  pricePositioning: number | null;
  affinity: number | null;
  sizeFit: number | null;
}

export interface MatchScore {
  score: number;           // 0..100
  breakdown: MatchBreakdown;
}

const WEIGHTS = { region: 45, theme: 10, pricePositioning: 10, affinity: 25, sizeFit: 10 } as const;

// Seuil d'affichage des suggestions « Pour vous » (note § 4).
export const MATCH_DISPLAY_THRESHOLD = 50;

interface StandLike {
  standType?: string;
  region?: string;
  musicStyles?: string[];
  experienceRange?: string; // premier | emergeant | confirme | expert
  maxDailyVolume?: string;
  priceRange?: string; // budget | medium | premium | luxury (panier moyen exposant)
}

interface FestivalLike {
  location?: string;
  region?: string;
  musicStyles?: string[];
  totalStands?: number;
  capacity?: number;
  expectedPriceRange?: string; // budget | medium | premium | luxury (panier moyen festivalier attendu)
}

// ── Région (45) ─────────────────────────────────────────────────────────
function scoreRegion(stand: StandLike, festival: FestivalLike): number | null {
  const standRegion = (stand.region || '').toLowerCase().trim();
  const festCity =
    (festival.region || festival.location?.split(',')[0] || '').toLowerCase().trim();
  if (!standRegion || !festCity) return null;

  if (festCity === standRegion) return 1;
  if (festCity.includes(standRegion) || standRegion.includes(festCity)) return 0.6;
  if (festCity.slice(0, 3) === standRegion.slice(0, 3)) return 0.3;
  return 0;
}

// ── Thème / style musical (10) ──────────────────────────────────────────
// Recouvrement des styles préférés de l'exposant avec ceux du festival.
// `mixed` (des deux côtés) = ouvert à tout → match plein.
function scoreTheme(stand: StandLike, festival: FestivalLike): number | null {
  const fest = (festival.musicStyles || []).filter(Boolean);
  const mine = (stand.musicStyles || []).filter(Boolean);
  if (fest.length === 0 || mine.length === 0) return null; // non musical / non renseigné

  if (mine.includes(MUSIC_STYLE_WILDCARD) || fest.includes(MUSIC_STYLE_WILDCARD)) return 1;

  const festSet = new Set(fest);
  const inter = mine.filter(s => festSet.has(s)).length;
  if (inter === 0) return 0;
  // Base 0.6 dès qu'il y a un style commun, +0.4 selon la couverture des styles
  // du festival (un exposant qui couvre tous les styles du festival = 1).
  return Math.min(1, 0.6 + 0.4 * (inter / fest.length));
}

// ── Positionnement prix (10) ────────────────────────────────────────────
// Compare le panier moyen que l'exposant facture (stand.priceRange) au panier
// moyen que le festival attend de ses festivaliers (festival.expectedPriceRange)
// — 4 paliers ordonnés identiques des deux côtés. Un festival "bohème / petit
// budget" matche mal un exposant haut de gamme, et inversement. Distance
// ordinale plutôt qu'égalité stricte : un écart d'un palier reste acceptable,
// deux paliers ou plus dégrade fortement (même logique gap-based que sizeFit).
const PRICE_RANK: Record<string, number> = { budget: 1, medium: 2, premium: 3, luxury: 4 };
function scorePricePositioning(stand: StandLike, festival: FestivalLike): number | null {
  const standRank = PRICE_RANK[stand.priceRange || ''];
  const festRank = PRICE_RANK[festival.expectedPriceRange || ''];
  if (!standRank || !festRank) return null; // l'un des deux non renseigné

  const gap = Math.abs(festRank - standRank);
  if (gap === 0) return 1;
  if (gap === 1) return 0.6;
  if (gap === 2) return 0.25;
  return 0;
}

// ── Affinité déclarée (25) ──────────────────────────────────────────────
// Préférence explicite de l'exposant : le festival fait-il partie de ses
// favoris ? Si l'exposant n'a AUCUN favori, le signal est absent (null) — on
// ne le pénalise pas pour ne pas avoir encore exploré la plateforme.
function scoreAffinity(festivalId: string | undefined, favorites: string[]): number | null {
  if (!favorites || favorites.length === 0) return null;
  return festivalId && favorites.includes(festivalId) ? 1 : 0;
}

// ── Adéquation de taille (10) ───────────────────────────────────────────
// Évite de suggérer un très gros festival à un exposant débutant/petit volume.
// Proxy : taille du festival (nb de stands) vs séniorité/volume de l'exposant.
const EXP_RANK: Record<string, number> = { premier: 1, emergeant: 2, confirme: 3, expert: 4 };
function scoreSizeFit(stand: StandLike, festival: FestivalLike): number | null {
  const festSize = festival.totalStands ?? festival.capacity;
  const expRank = EXP_RANK[stand.experienceRange || ''] || 0;
  if (!festSize || expRank === 0) return null; // taille inconnue ou séniorité non renseignée

  // Bucket festival : petit (<30), moyen (<80), gros (≥80).
  const festBucket = festSize < 30 ? 1 : festSize < 80 ? 2 : 3;
  // Un exposant peut « tenir » un festival de son niveau ou en dessous ; au-dessus
  // on dégrade doucement (jamais éliminatoire — c'est la Couche 1 qui élimine).
  const gap = festBucket - expRank; // >0 = festival plus gros que la séniorité
  if (gap <= 0) return 1;
  if (gap === 1) return 0.6;
  return 0.3;
}

/**
 * Score de pertinence 0..100 pour une paire (exposant, festival).
 * @param favorites  IDs de festivals mis en favori par l'exposant (affinité).
 */
export function getMatchScore(
  stand: StandLike,
  festival: FestivalLike & { id?: string },
  favorites: string[] = []
): MatchScore {
  const breakdown: MatchBreakdown = {
    region: scoreRegion(stand, festival),
    theme: scoreTheme(stand, festival),
    pricePositioning: scorePricePositioning(stand, festival),
    affinity: scoreAffinity(festival.id, favorites),
    sizeFit: scoreSizeFit(stand, festival),
  };

  // Moyenne pondérée sur les seuls critères PRÉSENTS (dénominateur dynamique).
  let weighted = 0;
  let totalWeight = 0;
  (Object.keys(WEIGHTS) as (keyof typeof WEIGHTS)[]).forEach(key => {
    const ratio = breakdown[key];
    if (ratio !== null) {
      weighted += WEIGHTS[key] * ratio;
      totalWeight += WEIGHTS[key];
    }
  });

  const score = totalWeight === 0 ? 0 : Math.round((weighted / totalWeight) * 100);
  return { score, breakdown };
}
